import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import {
  ShieldCheck,
  Lock,
  Eye,
  Database,
  Cpu,
  FileText,
  Mail,
  Phone,
  MapPin,
  CheckCircle2,
  AlertCircle,
  Scale
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const PrivacyPolicyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "August 16, 2026";

  const sections = [
    {
      id: "overview",
      title: "1. Overview & Entity Information",
      icon: ShieldCheck,
      content: (
        <div className="space-y-4">
          <p>
            Welcome to <strong className="text-white">Stellars Estates</strong> ("Company", "we", "our", or "us"). We operate as a premier digital marketing agency, performance advertising consultancy, AI automation engineering firm, and full-stack software development provider headquartered in <strong className="text-white">Delhi/NCR, India</strong> under the strategic leadership of Founder & Chief Growth Strategist <strong className="text-white">Sahil Sharma</strong>.
          </p>
          <p>
            This Privacy Policy sets forth our comprehensive practices regarding the collection, processing, storage, disclosure, and protection of personal data and business information when you visit our website (<a href="https://stellarsestates.in" className="text-[#F3453F] hover:underline">stellarsestates.in</a> / <a href="https://stellarsestates.com" className="text-[#F3453F] hover:underline">stellarsestates.com</a>), utilize our growth marketing services, engage with our AI conversational systems, or interact with our client advertising funnels.
          </p>
          <p>
            We are committed to operating in full compliance with applicable data privacy statutes, including the <strong className="text-white">Digital Personal Data Protection Act (DPDP Act, India)</strong>, the <strong className="text-white">General Data Protection Regulation (GDPR, EU/UK)</strong>, and global advertising platform data security standards (Meta, Google, LinkedIn).
          </p>
        </div>
      )
    },
    {
      id: "information-we-collect",
      title: "2. Information We Collect",
      icon: Database,
      content: (
        <div className="space-y-4">
          <p>
            We collect information strictly necessary to provide high-converting performance marketing campaigns, full-stack software development, automated customer relationship management (CRM) integrations, and strategic executive growth consultations.
          </p>
          
          <div className="space-y-3 pl-2">
            <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-2">
              <p className="text-sm font-bold text-white flex items-center gap-2">
                <CheckCircle2 size={16} className="text-[#F3453F]" />
                <span>A. Information Voluntarily Provided by You</span>
              </p>
              <ul className="list-disc list-inside text-xs sm:text-sm text-white/90 space-y-1 pl-2">
                <li><strong className="text-white">Contact & Identity Data:</strong> Full name, corporate email address (<code className="text-[#F3453F]">connect@stellarsestates.in</code>), phone / WhatsApp number, company name, and job title.</li>
                <li><strong className="text-white">Business & Campaign Specifications:</strong> Monthly advertising budget ranges, target customer acquisition cost (CAC), industry niche, current revenue milestones, sales team workflow details, and project objectives.</li>
                <li><strong className="text-white">Consultation & Form Submissions:</strong> Information submitted via our website inquiry forms, ROI calculators, quote requests, or strategic 1-on-1 strategy call bookings.</li>
              </ul>
            </div>

            <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-2">
              <p className="text-sm font-bold text-white flex items-center gap-2">
                <CheckCircle2 size={16} className="text-[#F3453F]" />
                <span>B. Information Collected Automatically</span>
              </p>
              <ul className="list-disc list-inside text-xs sm:text-sm text-white/90 space-y-1 pl-2">
                <li><strong className="text-white">Device & Technical Identifiers:</strong> IP address, browser type and version, operating system, device screen resolution, referring URLs, and geographic location (city and country level).</li>
                <li><strong className="text-white">Behavioral & Usage Data:</strong> Page visit duration, click-through rates, scroll depths, interaction with interactive calculators, and navigation paths.</li>
                <li><strong className="text-white">Cookies & First-Party Pixels:</strong> Session cookies, analytics tokens, and conversion tracking tags engineered for measurement and attribution.</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "how-we-use-data",
      title: "3. How We Process & Utilize Your Information",
      icon: Eye,
      content: (
        <div className="space-y-3">
          <p>We process personal and corporate data strictly for legitimate commercial and operational purposes, including:</p>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs sm:text-sm">
            <li className="p-3 rounded-lg bg-white/5 border border-white/10 flex items-start gap-2">
              <CheckCircle2 size={16} className="text-[#F3453F] flex-shrink-0 mt-0.5" />
              <span><strong className="text-white">Executing Client Growth Campaigns:</strong> Configuring hyper-targeted paid media across Meta, Google, YouTube, and programmatic ad channels.</span>
            </li>
            <li className="p-3 rounded-lg bg-white/5 border border-white/10 flex items-start gap-2">
              <CheckCircle2 size={16} className="text-[#F3453F] flex-shrink-0 mt-0.5" />
              <span><strong className="text-white">Autonomous AI Conversational Routing:</strong> Powering real-time WhatsApp bots to qualify inquiries and book consultation calls within seconds.</span>
            </li>
            <li className="p-3 rounded-lg bg-white/5 border border-white/10 flex items-start gap-2">
              <CheckCircle2 size={16} className="text-[#F3453F] flex-shrink-0 mt-0.5" />
              <span><strong className="text-white">CRM & Pipeline Synchronization:</strong> Routing qualified prospect data directly into designated enterprise CRM pipelines (HubSpot, Salesforce, Zoho).</span>
            </li>
            <li className="p-3 rounded-lg bg-white/5 border border-white/10 flex items-start gap-2">
              <CheckCircle2 size={16} className="text-[#F3453F] flex-shrink-0 mt-0.5" />
              <span><strong className="text-white">Transparent ROI & Attribution Analytics:</strong> Delivering verified multi-touch attribution reporting and ROAS calculations.</span>
            </li>
          </ul>
        </div>
      )
    },
    {
      id: "capi-and-advertising",
      title: "4. Advertising Technologies & Server-Side Tracking (CAPI)",
      icon: Cpu,
      content: (
        <div className="space-y-4">
          <p>
            To overcome modern browser privacy restrictions and signal loss, Stellars Estates deploys <strong className="text-white">First-Party Server-Side Conversion API (CAPI)</strong> architecture.
          </p>
          <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-xs sm:text-sm text-white/80 space-y-2">
            <p>
              When CAPI is utilized on client digital properties:
            </p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>Conversion milestones (such as form completions, qualified lead scores, and booked appointments) are transmitted securely from server to ad network endpoints via encrypted, hashed protocols (SHA-256).</li>
              <li>Data is processed strictly for ad optimization, measurement accuracy, and algorithmic lookalike audience generation.</li>
              <li>We never share cross-client data or pool customer lists across unrelated accounts.</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: "asset-ownership",
      title: "5. Client Data & 100% Asset Ownership Guarantee",
      icon: Lock,
      content: (
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-[#F3453F]/10 border border-[#F3453F]/30 text-white space-y-2">
            <p className="text-base font-bold text-white flex items-center gap-2">
              <ShieldCheck size={18} className="text-[#F3453F]" />
              <span>Unrestricted Client Ownership of All Data & Accounts</span>
            </p>
            <p className="text-xs sm:text-sm text-white/90 leading-relaxed">
              At Stellars Estates, our clients retain <strong className="text-white">100% unrestricted administrative ownership</strong> of all advertising accounts (Meta Ads Manager, Google Ads, LinkedIn Campaign Manager), custom tracking pixels, customer lead databases, CRM entries, and creative media assets.
            </p>
          </div>
          <p className="text-xs sm:text-sm text-white/90">
            We will <strong className="text-white">NEVER sell, rent, lease, or monetize your personal data or your prospective customers' data</strong> to third-party data brokers, marketing syndicates, or competitor organizations.
          </p>
        </div>
      )
    },
    {
      id: "ai-whatsapp",
      title: "6. Artificial Intelligence & Automated Messaging Systems",
      icon: Cpu,
      content: (
        <div className="space-y-3 text-xs sm:text-sm text-white/90">
          <p>
            Our web properties and client architectures utilize natural language conversational AI models and the official WhatsApp Business Platform API to automate customer engagement and provide sub-second speed-to-lead response times.
          </p>
          <ul className="list-disc list-inside space-y-1.5 pl-2">
            <li><strong className="text-white">Opt-In Consent:</strong> We only initiate automated WhatsApp or SMS messages when an explicit opt-in is provided by the prospect via our forms or direct messaging triggers.</li>
            <li><strong className="text-white">Opt-Out Mechanism:</strong> Recipients may opt out of automated messaging at any time by simply replying <strong className="text-white">"STOP"</strong> or contacting our team directly.</li>
            <li><strong className="text-white">AI Model Training Isolation:</strong> Client proprietary data and lead communications are processed through enterprise API tiers with strict zero-data-retention guarantees, ensuring data is never used to train public LLM models.</li>
          </ul>
        </div>
      )
    },
    {
      id: "data-retention-security",
      title: "7. Data Security, Encryption & Retention Protocols",
      icon: Lock,
      content: (
        <div className="space-y-4 text-xs sm:text-sm text-white/90">
          <p>
            We implement bank-grade administrative, technical, and physical safeguards to protect all data against unauthorized access, destruction, loss, or alteration.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="p-3.5 rounded-xl bg-white/5 border border-white/10 space-y-1">
              <p className="font-bold text-white text-xs">Encryption in Transit & Rest</p>
              <p className="text-[11px] text-white/90">TLS 1.3 encryption across all web traffic and AES-256 encryption for database storage.</p>
            </div>
            <div className="p-3.5 rounded-xl bg-white/5 border border-white/10 space-y-1">
              <p className="font-bold text-white text-xs">Role-Based Access Control</p>
              <p className="text-[11px] text-white/90">Strict multi-factor authentication (MFA) and least-privilege access restricted to authorized personnel.</p>
            </div>
            <div className="p-3.5 rounded-xl bg-white/5 border border-white/10 space-y-1">
              <p className="font-bold text-white text-xs">Disciplined Retention</p>
              <p className="text-[11px] text-white/90">Data is retained only as long as necessary to fulfill contractual growth mandates or statutory obligations.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "your-rights",
      title: "8. Your Legal Rights & Data Subject Access",
      icon: Scale,
      content: (
        <div className="space-y-3 text-xs sm:text-sm text-white/80">
          <p>Depending on your jurisdiction (including DPDP Act India and GDPR), you possess enforceable legal rights regarding your personal information:</p>
          <ul className="list-disc list-inside space-y-1.5 pl-2">
            <li><strong className="text-white">Right of Access & Confirmation:</strong> Request a copy of the personal data we hold about you.</li>
            <li><strong className="text-white">Right to Rectification:</strong> Request correction of inaccurate, outdated, or incomplete data.</li>
            <li><strong className="text-white">Right to Erasure ("Right to Be Forgotten"):</strong> Request deletion of your personal records, subject to statutory retention requirements.</li>
            <li><strong className="text-white">Right to Withdraw Consent:</strong> Revoke consent previously granted for marketing communications or automated workflows.</li>
          </ul>
          <p className="pt-2">
            To exercise any of these rights, email our Data Grievance Officer at <a href="mailto:connect@stellarsestates.in" className="text-[#F3453F] font-bold hover:underline">connect@stellarsestates.in</a>. We process verified requests within 15 business days.
          </p>
        </div>
      )
    },
    {
      id: "contact-officer",
      title: "9. Contact Us & Grievance Redressal",
      icon: Mail,
      content: (
        <div className="p-5 rounded-2xl bg-white/5 border border-white/10 space-y-4">
          <p className="text-xs sm:text-sm text-white/85">
            If you have questions, privacy inquiries, or wish to file a data handling grievance, please contact our designated Grievance & Compliance Officer:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs sm:text-sm">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-white">
                <MapPin size={16} className="text-[#F3453F]" />
                <span className="font-bold">Stellars Estates Corporate Headquarters</span>
              </div>
              <p className="text-white/70 pl-6">Raj Nagar Extension, Ghaziabad</p>
              
              <div className="flex items-center gap-2 text-white pt-2">
                <Phone size={16} className="text-[#F3453F]" />
                <span className="font-bold">Direct Phone / WhatsApp</span>
              </div>
              <p className="text-white/70 pl-6">+91 93109 71677</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-white">
                <Mail size={16} className="text-[#F3453F]" />
                <span className="font-bold">Official Privacy & Legal Email</span>
              </div>
              <p className="text-white/70 pl-6">
                <a href="mailto:connect@stellarsestates.in" className="text-[#F3453F] font-bold hover:underline">
                  connect@stellarsestates.in
                </a>
              </p>

              <div className="flex items-center gap-2 text-white pt-2">
                <ShieldCheck size={16} className="text-[#F3453F]" />
                <span className="font-bold">Strategic Leadership</span>
              </div>
              <p className="text-white/70 pl-6">Sahil Sharma, Founder & Chief Growth Strategist</p>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <>
      <Helmet>
        <title>Privacy Policy | Stellars Estates Digital Marketing Agency</title>
        <meta
          name="description"
          content="Review the official Privacy Policy of Stellars Estates. Learn how we protect client data, manage advertising attribution, adhere to DPDP & GDPR statutes, and uphold 100% data ownership."
        />
        <link rel="canonical" href="https://stellarsestates.in/privacy-policy" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Privacy Policy | Stellars Estates Digital Marketing Agency" />
        <meta property="og:description" content="Review the official Privacy Policy of Stellars Estates. Learn how we protect client data, manage advertising attribution, adhere to DPDP & GDPR statutes, and uphold 100% data ownership." />
        <meta property="og:image" content="https://i.postimg.cc/3NNd39Q1/social-sharing-image-stallars-estates.jpg" />
        <meta property="og:image:secure_url" content="https://i.postimg.cc/3NNd39Q1/social-sharing-image-stallars-estates.jpg" />
        <meta property="og:image:alt" content="Stellars Estates Privacy Policy" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://i.postimg.cc/3NNd39Q1/social-sharing-image-stallars-estates.jpg" />
      </Helmet>

      <div className="min-h-screen bg-[#181F32]">
        <Header />

        <main className="pt-12 pb-24">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
            
            {/* Header Title Section */}
            <div className="glass-panel-hero p-8 sm:p-12 text-center space-y-4">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#F3453F]/15 border border-[#F3453F]/40 text-[#F3453F] text-xs font-bold tracking-wider uppercase">
                <Lock size={14} />
                <span>Legal & Data Compliance</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white font-heading">
                Privacy <span className="text-[#F3453F]">Policy</span>
              </h1>
              <p className="text-white/80 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
                Our strict commitment to transparent data handling, client asset ownership, advertising privacy compliance, and personal data protection.
              </p>
              <div className="pt-2 text-xs font-mono text-white/50">
                Effective Date & Last Updated: {lastUpdated}
              </div>
            </div>

            {/* Quick Summary Highlights Banner */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="glass-panel p-5 border border-white/10 space-y-2">
                <div className="w-8 h-8 rounded-lg bg-[#F3453F]/20 flex items-center justify-center text-[#F3453F]">
                  <ShieldCheck size={18} />
                </div>
                <h3 className="font-bold text-white text-sm">100% Asset Ownership</h3>
                <p className="text-xs text-white/70">You retain complete ownership of all ad accounts, customer databases, and tracking pixels.</p>
              </div>
              <div className="glass-panel p-5 border border-white/10 space-y-2">
                <div className="w-8 h-8 rounded-lg bg-[#F3453F]/20 flex items-center justify-center text-[#F3453F]">
                  <Lock size={18} />
                </div>
                <h3 className="font-bold text-white text-sm">Zero Data Selling</h3>
                <p className="text-xs text-white/70">We never sell, broker, or syndicate your business data or lead information to third parties.</p>
              </div>
              <div className="glass-panel p-5 border border-white/10 space-y-2">
                <div className="w-8 h-8 rounded-lg bg-[#F3453F]/20 flex items-center justify-center text-[#F3453F]">
                  <Cpu size={18} />
                </div>
                <h3 className="font-bold text-white text-sm">Secure CAPI Attribution</h3>
                <p className="text-xs text-white/70">First-party server-side tracking engineered strictly with SHA-256 encrypted protocols.</p>
              </div>
            </div>

            {/* Detailed Policy Sections */}
            <div className="space-y-8">
              {sections.map((sec) => {
                const Icon = sec.icon;
                return (
                  <motion.section
                    key={sec.id}
                    id={sec.id}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="glass-panel p-6 sm:p-8 space-y-4 border border-white/10 hover:border-white/20 transition-colors"
                  >
                    <div className="flex items-center gap-3 border-b border-white/10 pb-4">
                      <div className="w-10 h-10 rounded-xl bg-[#F3453F]/15 border border-[#F3453F]/40 flex items-center justify-center text-[#F3453F]">
                        <Icon size={20} />
                      </div>
                      <h2 className="text-xl sm:text-2xl font-bold text-white font-heading">
                        {sec.title}
                      </h2>
                    </div>
                    <div className="text-white/85 text-sm sm:text-base leading-relaxed font-sans">
                      {sec.content}
                    </div>
                  </motion.section>
                );
              })}
            </div>

            {/* Bottom Support Callout */}
            <div className="p-6 rounded-2xl bg-[#202942] border border-white/15 text-center space-y-3">
              <h3 className="text-lg font-bold text-white font-heading">Have questions regarding our privacy practices?</h3>
              <p className="text-xs sm:text-sm text-white/80 max-w-xl mx-auto">
                Reach out directly to our leadership and data compliance team at <a href="mailto:connect@stellarsestates.in" className="text-[#F3453F] font-bold hover:underline">connect@stellarsestates.in</a> or call <span className="text-white font-bold">+91 95400 39394</span>.
              </p>
            </div>

          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default PrivacyPolicyPage;
